// Global input machine for the board: one place that turns raw
// pointer/wheel events into pan, pinch-zoom, note-drag, or tap.
//
//   1 pointer on board  → pan          1 pointer on own note → drag it
//   2 pointers          → pinch-zoom   quick press+release   → tap
//   wheel               → pan          ctrl/⌘+wheel (pinch)  → zoom at cursor

import type { Camera } from "@ts/board/camera.ts";
import { panBy, pinch, screenToWorld, zoomAt } from "@ts/board/camera.ts";

export interface InputCallbacks {
  getCamera(): Camera;
  setCamera(c: Camera): void;
  /** May this note be dragged (owned + board not frozen)? */
  canDrag(noteId: string): boolean;
  onTapNote(noteId: string): void;
  /** Live while dragging (render only). */
  onNoteMoved(noteId: string, wx: number, wy: number): void;
  /** Drag finished (persist). */
  onNoteDropped(noteId: string, wx: number, wy: number): void;
  getNotePos(noteId: string): { x: number; y: number };
}

const TAP_SLOP = 6;

export function attachInput(stage: HTMLElement, cb: InputCallbacks): void {
  type Mode = "idle" | "pending" | "pan" | "drag" | "pinch";
  const pointers = new Map<number, { x: number; y: number }>();
  let mode: Mode = "idle";
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let dragId: string | null = null;
  let dragEl: HTMLElement | null = null;
  let grabDX = 0;
  let grabDY = 0;
  let lastWorld = { x: 0, y: 0 };
  let pinchPrev: [{ x: number; y: number }, { x: number; y: number }] | null = null;

  const noteFromTarget = (t: EventTarget | null): HTMLElement | null =>
    t instanceof Element ? t.closest<HTMLElement>(".note[data-note-id]") : null;

  stage.addEventListener("pointerdown", (e) => {
    // let real controls (audio buttons etc.) behave normally
    if (e.target instanceof Element && e.target.closest("button, a, audio, select, input, textarea"))
      return;
    stage.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size === 1) {
      mode = "pending";
      startX = lastX = e.clientX;
      startY = lastY = e.clientY;
      const noteEl = noteFromTarget(e.target);
      dragId = noteEl?.dataset.noteId ?? null;
      dragEl = noteEl;
    } else if (pointers.size === 2) {
      if (mode === "drag" && dragId) {
        dragEl?.classList.remove("dragging");
        cb.onNoteDropped(dragId, lastWorld.x + grabDX, lastWorld.y + grabDY);
      }
      dragId = null;
      dragEl = null;
      mode = "pinch";
      pinchPrev = [...pointers.values()] as [any, any];
    }
  });

  stage.addEventListener("pointermove", (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (mode === "pending" && Math.hypot(e.clientX - startX, e.clientY - startY) > TAP_SLOP) {
      if (dragId && cb.canDrag(dragId)) {
        mode = "drag";
        const w = screenToWorld(cb.getCamera(), e.clientX, e.clientY);
        const p = cb.getNotePos(dragId);
        grabDX = p.x - w.x;
        grabDY = p.y - w.y;
        dragEl?.classList.add("dragging");
      } else {
        mode = "pan";
      }
    }

    if (mode === "pan") {
      cb.setCamera(panBy(cb.getCamera(), e.clientX - lastX, e.clientY - lastY));
    } else if (mode === "drag" && dragId) {
      lastWorld = screenToWorld(cb.getCamera(), e.clientX, e.clientY);
      cb.onNoteMoved(dragId, lastWorld.x + grabDX, lastWorld.y + grabDY);
    } else if (mode === "pinch" && pointers.size >= 2 && pinchPrev) {
      const [a, b] = [...pointers.values()];
      cb.setCamera(pinch(cb.getCamera(), pinchPrev[0], pinchPrev[1], a, b));
      pinchPrev = [a, b];
    }

    lastX = e.clientX;
    lastY = e.clientY;
  });

  const finish = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);

    if (mode === "pending" && dragId) {
      cb.onTapNote(dragId);
    } else if (mode === "drag" && dragId) {
      dragEl?.classList.remove("dragging");
      cb.onNoteDropped(dragId, lastWorld.x + grabDX, lastWorld.y + grabDY);
    }

    if (pointers.size === 1) {
      // pinch → single-finger pan, re-anchored
      const rest = [...pointers.values()][0];
      mode = "pan";
      lastX = rest.x;
      lastY = rest.y;
      pinchPrev = null;
    } else if (pointers.size === 0) {
      mode = "idle";
      dragId = null;
      dragEl = null;
      pinchPrev = null;
    }
  };
  stage.addEventListener("pointerup", finish);
  stage.addEventListener("pointercancel", finish);

  stage.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        cb.setCamera(zoomAt(cb.getCamera(), e.clientX, e.clientY, Math.exp(-e.deltaY * 0.01)));
      } else {
        cb.setCamera(panBy(cb.getCamera(), -e.deltaX, -e.deltaY));
      }
    },
    { passive: false }
  );
}
